import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemButton,
  Skeleton
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  PhoneAndroid as MobileIcon,
  DesktopWindows as DesktopIcon,
  Slideshow as PresentationIcon,
  Print as PrintIcon,
  Theaters as CinemaIcon,
  Image as ImageIcon,
  AspectRatio as AspectRatioIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import processService from '../services/processService';

// Map icon names to components
const platformIcons = {
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  linkedin: LinkedInIcon,
  twitter: TwitterIcon,
  mobile: MobileIcon,
  desktop: DesktopIcon,
  presentation: PresentationIcon,
  print: PrintIcon,
  cinema: CinemaIcon
};

const CroppedResults = ({ crops, jobId, isLoading }) => {
  const [selectedCrop, setSelectedCrop] = useState(null);
  
  // Set initial selected crop when crops load
  useEffect(() => {
    if (crops && crops.length > 0 && !selectedCrop) {
      setSelectedCrop(crops[0]);
    }
  }, [crops, selectedCrop]);
  
  if (!crops && !isLoading) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <Paper
        sx={{
          background: 'rgba(30, 41, 59, 0.6)',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box 
          sx={{ 
            p: 3, 
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AspectRatioIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Cropped Variations
            </Typography>
            {crops && (
              <Chip 
                label={`${crops.length} formats`} 
                size="small" 
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </Box>
        
        {/* Main Content Area */}
        <Box sx={{ display: 'flex', height: 600 }}>
          {/* Left: Thumbnail List */}
          <Box
            sx={{
              width: 180,
              borderRight: '1px solid rgba(255, 255, 255, 0.1)',
              overflowY: 'auto',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              '&::-webkit-scrollbar': {
                width: 8
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'rgba(148, 163, 184, 0.1)'
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'rgba(148, 163, 184, 0.3)',
                borderRadius: 4,
                '&:hover': {
                  bgcolor: 'rgba(148, 163, 184, 0.5)'
                }
              }
            }}
          >
            {isLoading ? (
              <Box sx={{ p: 1 }}>
                {[...Array(6)].map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    height={80}
                    sx={{ mb: 1, borderRadius: 1 }}
                  />
                ))}
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {crops?.map((crop, index) => {
                  const isSelected = selectedCrop?.filename === crop.filename;
                  
                  return (
                    <ListItem key={crop.filename} disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => setSelectedCrop(crop)}
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          p: 1.5,
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(99, 102, 241, 0.1)',
                            borderLeft: '3px solid',
                            borderLeftColor: 'primary.main',
                            '&:hover': {
                              bgcolor: 'rgba(99, 102, 241, 0.15)'
                            }
                          }
                        }}
                      >
                        {/* Thumbnail */}
                        <Box
                          sx={{
                            width: '100%',
                            height: 80,
                            bgcolor: 'rgba(0, 0, 0, 0.3)',
                            borderRadius: 1,
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 1
                          }}
                        >
                          <img
                            src={crop.path}
                            alt={crop.ratioName}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                        
                        {/* Label */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {crop.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {crop.aspectRatio}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
          
          {/* Center: Main Preview */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              bgcolor: 'rgba(0, 0, 0, 0.1)'
            }}
          >
            {isLoading ? (
              <Skeleton
                variant="rectangular"
                width="80%"
                height="80%"
                sx={{ borderRadius: 2 }}
              />
            ) : selectedCrop ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedCrop.filename}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <img
                      src={selectedCrop.path}
                      alt={selectedCrop.ratioName}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '450px',
                        objectFit: 'contain',
                        borderRadius: '4px'
                      }}
                    />
                  </Box>
                </motion.div>
              </AnimatePresence>
            ) : (
              <Typography color="text.secondary">
                Select a format to preview
              </Typography>
            )}
          </Box>
          
          {/* Right: Details Panel */}
          <Box
            sx={{
              width: 320,
              borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'rgba(0, 0, 0, 0.2)'
            }}
          >
            {isLoading ? (
              <>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={48} />
              </>
            ) : selectedCrop ? (
              <>
                {/* Platform Info */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedCrop.displayName}
                  </Typography>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                {/* Specifications */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, opacity: 0.7 }}>
                    SPECIFICATIONS
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Aspect Ratio
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedCrop.aspectRatio}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Format
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedCrop.filename.endsWith('.png') ? 'PNG' : 'JPEG'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                {/* Best Used For */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, opacity: 0.7 }}>
                    BEST USED FOR
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedCrop.platforms?.map(platform => {
                      const IconComponent = platformIcons[platform.icon] || ImageIcon;
                      return (
                        <Chip
                          key={platform.name}
                          icon={<IconComponent sx={{ fontSize: 16 }} />}
                          label={platform.use_case}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(99, 102, 241, 0.1)',
                            border: '1px solid rgba(99, 102, 241, 0.3)',
                            '& .MuiChip-icon': {
                              color: 'primary.main'
                            }
                          }}
                        />
                      );
                    }) || (
                      <Chip
                        label="General Use"
                        size="small"
                        sx={{
                          bgcolor: 'rgba(99, 102, 241, 0.1)',
                          border: '1px solid rgba(99, 102, 241, 0.3)'
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                Select a format to view details
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
};

export default CroppedResults;